import { Fetcher } from '@this/src/util';
/* eslint-disable max-lines */
import React, { useState, useEffect } from 'react';
import type { Themes } from '@this/constants/themes';
import { styled } from '@this/constants/themes';
import { observer } from 'mobx-react';
import Container from '@material-ui/core/Container';
import Box from '@material-ui/core/Box';
import Card from '@material-ui/core/Card';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import List from '@material-ui/core/List';
import type { ListItemTypeMap } from '@material-ui/core/ListItem';
import ListItem from '@material-ui/core/ListItem';
import type { OverridableComponent } from '@material-ui/core/OverridableComponent';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import Tooltip from '@material-ui/core/Tooltip';
import CheckIcon from '@material-ui/icons/Check';
import type { Plan } from '@this/components/appoint/common';
import { H2, H3, LoadingBackdrop, ButtonProgress } from '@this/components/appoint/common';
import muiTheme from '@this/components/appoint/mui_theme';
import type { CreditBalanceHistoryResponse } from '@this/domain/credit_balance_history';
import CreditBalanceHistory, {
  convertCreditBalanceHistoryResponseToArgs
} from '@this/domain/credit_balance_history';
import type { Stripe } from '@stripe/stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import Utils from '@this/domain/appoint/utils';
import CardSetupForm from './CardSetupForm';

interface Response {
  member_count: number;
  price: number;
  current_plan: Plan;
  current_plan_text: string;
  in_trial_term: boolean;
  card_registered: boolean;
  trial_used: boolean;
  credit_balance_histories: CreditBalanceHistoryResponse[];
  stripe_pk: string;
}

const Payments = observer(() => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showTrialModal, setShowTrialModal] = useState<boolean>(false);
  const [showPaymentModal, setShowPaymentModal] = useState<boolean>(false);
  const [memberCount, setMemberCount] = useState<number | null>(null);
  const [price, setPrice] = useState<number | null>(null);
  const [currentPlan, setCurrentPlan] = useState<Plan>('free');
  const [currentPlanText, setCurrentPlanText] = useState<string>('');
  const [inTrialTerm, setInTrialTerm] = useState<boolean>(false);
  const [cardRegistered, setCardRegistered] = useState<boolean>(false);
  const [trialUsed, setTrialUsed] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [histories, setHistories] = useState<CreditBalanceHistory[]>([]);
  const [showFreeModal, setShowFreeModal] = useState<boolean>(false);
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);

  const fetchData = async () => {
    setIsLoading(true);
    const res = await Fetcher.get<Response>('/aicalendar/organization/payments.json');
    setMemberCount(res.member_count);
    setPrice(res.price);
    setCurrentPlan(res.current_plan);
    setCurrentPlanText(res.current_plan_text);
    setInTrialTerm(res.in_trial_term);
    setCardRegistered(res.card_registered);
    setTrialUsed(res.trial_used);
    setHistories(
      res.credit_balance_histories.map(h => {
        return new CreditBalanceHistory(convertCreditBalanceHistoryResponseToArgs(h));
      })
    );

    setIsLoading(false);

    if (!stripePromise) {
      setStripePromise(loadStripe(res.stripe_pk));
    }
  };

  useEffect(() => {
    fetchData();
    Utils.trackEvent('[cal][view] organization payments');
  }, []);

  const handleTrialModalOpen = () => {
    Utils.trackEvent('[cal] show trial modal');
    setShowTrialModal(true);
  };

  const handlePlanSelected = async (plan: Plan) => {
    setIsSubmitting(true);
    await Fetcher.post('/aicalendar/organization/payments.json', { plan });
    setIsSubmitting(false);
  };

  const handleTrialClicked = async () => {
    Utils.trackEvent('[cal] trial click');
    await handlePlanSelected('pro');
    setShowTrialModal(false);
    fetchData();
  };

  const handlePaymentModalOpen = () => {
    Utils.trackEvent('[cal] show payment modal');
    setShowPaymentModal(true);
  };

  const handleFreeModalOpen = () => {
    Utils.trackEvent('[cal] show free modal');
    setShowFreeModal(true);
  };

  const handleCardSubmit = () => {
    Utils.trackEvent('[cal] click card back');
    setShowPaymentModal(false);
    fetchData();
  };

  const handleChangeToFreePlan = async () => {
    Utils.trackEvent('[cal] change to free click');
    await handlePlanSelected('free');
    setShowFreeModal(false);
    fetchData();
  };

  const renderTrialDialog = () => (
    <Dialog open={showTrialModal} fullWidth maxWidth="sm" onClose={() => setShowTrialModal(false)}>
      <Box padding="27px 24px">
        <H3>Proプランに変更しますか？</H3>
        <List dense>
          <ListItem>
            <ListItemText primary="Proプランの機能が30日間無料で、クレジットカード登録なしでご利用いただけます。" />
          </ListItem>
          <ListItem>
            <ListItemText primary="無料期間終了後も継続してProプランの機能をご利用いただくには、クレジットカード登録が必要です。" />
          </ListItem>
          <ListItem>
            <ListItemText primary="クレジットカード登録の前に無料期間が終了した場合は、自動でFreeプランに切り替わります。" />
          </ListItem>
        </List>
        <Box marginTop="20px" textAlign="right">
          <Button color="primary" style={{ marginRight: '20px' }} onClick={() => setShowTrialModal(false)}>
            キャンセル
          </Button>
          <Button color="primary" variant="contained" disabled={isSubmitting} onClick={() => handleTrialClicked()}>
            Proプランを試す
          </Button>
          {isSubmitting && <ButtonProgress />}
        </Box>
      </Box>
    </Dialog>
  );

  const renderPaymentDialog = () => (
    <Dialog open={showPaymentModal} fullWidth maxWidth="sm" onClose={() => setShowPaymentModal(false)}>
      <Box padding="27px 24px">
        <H3>クレジットカード登録</H3>
        {memberCount && (
          <>
            <Typography color="textSecondary" style={{ fontSize: '14px' }}>
              現在のユーザー数
            </Typography>
            <Typography style={{ fontSize: '16px' }}>{memberCount}人</Typography>
          </>
        )}
        {price && (
          <>
            <Typography color="textSecondary" style={{ fontSize: '14px', marginTop: '25px' }}>
              お支払総額
            </Typography>
            <Typography style={{ fontSize: '18px' }}>{`${utils.formatPrice(price)}（税込）`}</Typography>
            <Typography color="textSecondary" style={{ fontSize: '14px', marginTop: '10px' }}>
              ※税込、1ヶ月あたり
              <br />
              ※翌月以降は自動更新されます
            </Typography>
          </>
        )}
        <CreditCardInputWrap>
          <Elements stripe={stripePromise}>
            <CardSetupForm handleSucceeded={() => handleCardSubmit()} />
          </Elements>
        </CreditCardInputWrap>
      </Box>
    </Dialog>
  );

  const renderFreeDialog = () => (
    <Dialog open={showFreeModal} fullWidth maxWidth="sm" onClose={() => setShowFreeModal(false)}>
      <Box padding="20px">
        <H3>Freeプランにダウングレードしますか？</H3>
        <List dense>
          <ListItem>
            <ListItemText primary="変更後も、現在のプランの有効期限までは有料機能がご利用いただけます。" />
          </ListItem>
          <ListItem>
            <ListItemText primary="クレジットポイント残高がプラスの場合は、ダウングレードした時点で破棄されます。" />
          </ListItem>
          <ListItem>
            <ListItemText primary="クレジットポイント残高がマイナスの場合は、ダウングレードした時点で不足分がクレジットカードから引き落とされます。" />
          </ListItem>
        </List>
        <Box marginTop="20px" textAlign="right">
          <Button color="primary" style={{ marginRight: '20px' }} onClick={() => setShowFreeModal(false)}>
            キャンセル
          </Button>
          <Button variant="contained" color="primary" onClick={() => handleChangeToFreePlan()}>
            ダウングレードする
          </Button>
        </Box>
      </Box>
    </Dialog>
  );

  const renderHistoryTable = () => (
    <TableContainer style={{ marginTop: '32px' }} component={Paper}>
      <H3 style={{ padding: '27px 24px 0' }}>決済履歴</H3>
      {histories.length > 0 ? (
        <Table>
          <TableHead>
            <TableRow>
              <HistoryHeadCell style={{ paddingLeft: '24px' }}>日時</HistoryHeadCell>
              <HistoryHeadCell>内容</HistoryHeadCell>
              <HistoryHeadCell>有効期限</HistoryHeadCell>
              <HistoryHeadCell>請求額</HistoryHeadCell>
              <HistoryHeadCell>お支払額</HistoryHeadCell>
              <HistoryHeadCell>
                クレジット
                <br />
                ポイント残高
              </HistoryHeadCell>
              <HistoryHeadCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {histories.map(r => (
              <TableRow key={r.id}>
                <HistoryCell style={{ paddingLeft: '24px' }}>{r.date.format('YYYY/MM/DD hh:mm')}</HistoryCell>
                <HistoryCell>{r.description}</HistoryCell>
                <HistoryCell>{r.expireAt?.format('YYYY/MM/DD')}</HistoryCell>
                <HistoryCell align="right">
                  {typeof r.billedAmount === 'number' ? utils.formatPrice(r.billedAmount) : '0円'}
                </HistoryCell>
                <HistoryCell align="right">
                  {typeof r.paidAmount === 'number' ? utils.formatPrice(r.paidAmount) : '0円'}
                </HistoryCell>
                <HistoryCell align="right">{utils.formatPrice(r.balance)}</HistoryCell>
                <HistoryCell align="right">
                  {r.paidAmount && <a href={`/aicalendar/organization/payment_receipts/${r.id}.pdf`}>領収書</a>}
                </HistoryCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Typography color="textSecondary" style={{ fontSize: '16px', padding: '10px 24px 24px' }}>
          お支払履歴はまだありません
        </Typography>
      )}
    </TableContainer>
  );

  return (
    <Container maxWidth="md">
      <LoadingBackdrop open={isLoading} />
      <TheH2>ご利用プランとお支払い</TheH2>
      <Paper style={{ marginBottom: '30px', padding: '20px 24px 24px' }}>
        <H3>現在のプラン</H3>
        {currentPlanText.split('\n').map((line, i) => (
          <Typography
            key={i.toString()}
            color={i === 0 ? 'textPrimary' : 'textSecondary'}
            style={{ marginTop: '8px' }}
          >
            {line}
          </Typography>
        ))}
      </Paper>
      <Box display="flex">
        <PlanCard style={{ marginRight: '24px' }}>
          <FreeHead bgcolor={muiTheme.palette.grey[200]}>
            <PlanTitle>AIカレンダー Free</PlanTitle>
            <PlanSubTitle>無料でお試し</PlanSubTitle>
            <PlanPriceArea>
              <PlanPrice>0円</PlanPrice>
              <PlanPriceUnit>/ 月</PlanPriceUnit>
            </PlanPriceArea>
          </FreeHead>
          <PlanBody>
            <PlanDescription>
              <List dense>
                <PlanListItem>
                  <PlanListItemIcon>
                    <CheckIcon />
                  </PlanListItemIcon>
                  <ListItemText primary="作成できる打ち合わせ 無制限" />
                </PlanListItem>
                <PlanListItem>
                  <PlanListItemIcon>
                    <CheckIcon />
                  </PlanListItemIcon>
                  <ListItemText primary="候補日時のカレンダー自動登録＆削除" />
                </PlanListItem>
                <PlanListItem>
                  <PlanListItemIcon>
                    <CheckIcon />
                  </PlanListItemIcon>
                  <ListItemText primary="打ち合わせに設定できる参加者 1名まで" />
                </PlanListItem>
              </List>
            </PlanDescription>
            {currentPlan === 'free' ? (
              <PlanSelectButton variant="outlined" color="primary" disabled>
                現在ご利用中
              </PlanSelectButton>
            ) : currentPlan === 'pro' ? (
              <>
                {inTrialTerm && !cardRegistered ? (
                  <Tooltip title="トライアルが終了すると自動的にFreeプランに切り替わります" placement="top">
                    <div>
                      <PlanSelectButton variant="outlined" color="primary" disabled>
                        Proプラントライアル中
                      </PlanSelectButton>
                    </div>
                  </Tooltip>
                ) : (
                  <PlanSelectButton variant="contained" color="primary" onClick={() => handleFreeModalOpen()}>
                    プランを変更する
                  </PlanSelectButton>
                )}
              </>
            ) : null}
          </PlanBody>
        </PlanCard>
        <PlanCard>
          <ProHead bgcolor={muiTheme.palette.primary.main} style={{ color: 'white' }}>
            <PlanTitle style={{ color: 'white' }}>AIカレンダー Pro</PlanTitle>
            <PlanSubTitle>チームで活用するなら</PlanSubTitle>
            <PlanPriceArea>
              <PlanPrice>1,980円</PlanPrice>
              <PlanPriceUnit>/ 月 （5名まで）</PlanPriceUnit>
            </PlanPriceArea>
          </ProHead>
          <PlanBody>
            <PlanDescription>
              <List dense>
                <PlanListItem>
                  <PlanListItemIcon>
                    <CheckIcon />
                  </PlanListItemIcon>
                  <ListItemText primary="作成できる打ち合わせ 無制限" />
                </PlanListItem>
                <PlanListItem>
                  <PlanListItemIcon>
                    <CheckIcon />
                  </PlanListItemIcon>
                  <ListItemText primary="候補日時のカレンダー自動登録＆削除" />
                </PlanListItem>
                <PlanListItem>
                  <PlanListItemIcon>
                    <CheckIcon />
                  </PlanListItemIcon>
                  <ListItemText primary="打ち合わせに設定できる参加者 無制限" />
                </PlanListItem>
                <PlanListItem>
                  <PlanListItemIcon>
                    <CheckIcon />
                  </PlanListItemIcon>
                  <ListItemText
                    primary="今後予定している機能追加対応"
                    secondary={
                      <>
                        経費精算サービス連携
                        <br />
                        SFA連携
                        <br />
                        出張管理サービス連携
                      </>
                    }
                    classes={{ secondary: 'secondary_text' }}
                  />
                </PlanListItem>
              </List>
            </PlanDescription>
            {currentPlan === 'free' ? (
              <>
                {trialUsed ? (
                  <PlanSelectButton variant="contained" color="primary" onClick={() => handlePaymentModalOpen()}>
                    プランを変更する
                  </PlanSelectButton>
                ) : (
                  <PlanSelectButton variant="contained" color="primary" onClick={() => handleTrialModalOpen()}>
                    30日間無料で試す
                  </PlanSelectButton>
                )}
              </>
            ) : currentPlan === 'pro' ? (
              <>
                {cardRegistered ? (
                  <PlanSelectButton variant="outlined" color="primary" disabled>
                    現在ご利用中
                  </PlanSelectButton>
                ) : (
                  <PlanSelectButton variant="contained" color="primary" onClick={() => handlePaymentModalOpen()}>
                    継続利用する
                  </PlanSelectButton>
                )}
              </>
            ) : null}
          </PlanBody>
        </PlanCard>
      </Box>

      {renderHistoryTable()}

      <Paper style={{ marginTop: '32px', padding: '20px 24px 24px' }}>
        <H3>有料プランについて</H3>
        <List dense>
          <TheListItem>AIカレンダーProプランは、有料で上記の追加機能がご利用いただけるプランです。</TheListItem>
          <TheListItem>
            登録ユーザー数の増減の際は、日割りで実際にご利用いただいた日数に応じて料金が発生します。
          </TheListItem>
          <TheListItem>
            クレジットカードの引き落とし回数を可能な限り更新時の月1回に抑えるため、「クレジットポイント」という仕組みを採用しています。ユーザー減少の際はクレジットポイントとして払い戻し、ユーザーの追加または更新の際にクレジットポイントを利用して料金を支払うことができます。
          </TheListItem>
          <TheListItem>
            クレジットポイントには貨幣価値または交換価値がなく、譲渡や返金はできません。また、有料プランの終了に伴い失効します。
          </TheListItem>
        </List>

        <H3 style={{ marginTop: '24px' }}>料金の発生と決済のタイミングについて</H3>
        <List dense>
          <TheListItem>トライアル期間中は、ユーザーを追加しても料金が発生しません。</TheListItem>
          <TheListItem>
            クレジットカード登録をした日に、有効なユーザー分の料金がクレジットカードから引き落とされます。
          </TheListItem>
          <TheListItem>
            Freeプランにダウングレードしない限り、有効期間の最終日に自動で更新され、クレジットカードから引き落とされます。
          </TheListItem>
          <TheListItem>
            有料プランを利用中にユーザーを無効化すると、次の更新日までの日割り料金がクレジットポイント残高に追加されます。
            <br />
            例えば、有料プランの契約日または更新日から、次の更新日までの日数が30日で、そのうち15日が経過した時点であるユーザーを無効化すると、そのユーザーに対する支払い済みの月額料金（税込）の50%がクレジットポイント残高に追加されます。
          </TheListItem>
          <TheListItem>
            有料プランを利用中に追加のユーザーを招待すると、次こ更新日までの日割り料金がクレジットポイント残高からマイナスされます。クレジットポイント残高が不足している場合は、次の更新時に合わせてクレジットカードから引き落とされます。
          </TheListItem>
          <TheListItem>
            クレジットポイント残高がプラスの状態で無料プランにダウングレードすると、その時点でクレジットポイント残高が破棄されます。
          </TheListItem>
          <TheListItem>
            クレジットポイント残高がマイナスの状態で無料プランにダウングレードすると、その時点で不足分がクレジットカードから引き落とされます。
          </TheListItem>
        </List>
      </Paper>

      {renderTrialDialog()}
      {renderPaymentDialog()}
      {renderFreeDialog()}
    </Container>
  );
});

const TheH2 = styled(H2)`
  margin-bottom: 30px;
  font-size: 24px;
  line-height: 32px;
`;

const PlanCard = styled(Card)`
  flex-grow: 10;
  flex-basis: 250px;
`;

const ThePlanHead = styled(Box)`
  position: relative;
  overflow: hidden;
  min-height: 185px;
  padding: 32px 24px;

  &::after {
    content: '';
    display: block;
    position: absolute;
    width: 286px;
    height: 286px;
    background: ${muiTheme.palette.primary.light};
    border-radius: 50%;
    opacity: 0.28;
  }
`;

const FreeHead = styled(ThePlanHead)`
  background-color: #93a2aa;

  &::after {
    top: 70px;
    right: -52px;
  }
`;

const ProHead = styled(ThePlanHead)`
  &::before {
    content: '';
    display: block;
    position: absolute;
    top: -100px;
    right: 34px;
    width: 224px;
    height: 224px;
    background: #3ac8bb;
    border-radius: 50%;
    opacity: 0.16;
  }

  &::after {
    top: 64px;
    right: -64px;
  }
`;

const PlanTitle = styled(H3)`
  margin-bottom: 16px;
  font-size: 24px;
  line-height: 32px;
`;

const PlanSubTitle = styled(Typography)`
  font-size: 16px;
  line-height: 22px;
`;

const PlanPriceArea = styled(Box)`
  display: flex;
  align-items: flex-end;
  width: fit-content;
  margin-top: 43px;
`;

const PlanPrice = styled(Typography)`
  font-size: 24px;
  font-weight: 500;
  line-height: 33px;
`;

const PlanPriceUnit = styled(Typography)`
  margin-left: 8px;
  font-size: 16px;
  font-weight: 500;
  line-height: 28px;
`;

const PlanBody = styled(Box)`
  padding: 20px 10px 32px;
  text-align: center;
`;

const PlanDescription = styled(Box)`
  min-height: 278px;
`;

const PlanListItem = styled(ListItem).attrs({ button: undefined })`
  align-items: flex-start;

  .secondary_text {
    margin-top: 9px;
  }
` as OverridableComponent<ListItemTypeMap<{}, 'li'>>;

const PlanListItemIcon = styled(ListItemIcon)`
  min-width: 33px;
  padding-top: 2px;
`;

const PlanSelectButton = styled(Button).attrs(props => ({ variant: props.variant, color: 'primary' }))`
  bottom: 0;
  min-width: 240px;
`;

const CreditCardInputWrap = styled(Box)`
  margin-top: 24px;
  padding-top: 32px;
  border-top: solid 1px ${props => props.theme.grayBorderColor};
`;

const HistoryCell = styled(TableCell)`
  font-size: 14px;
  line-height: 21px;
`;

const HistoryHeadCell = styled(HistoryCell)`
  padding-top: 9px;
  padding-bottom: 9px;
`;

const TheListItem = styled(ListItem).attrs<{ button: undefined }>({ button: undefined })<{ theme: Themes }>`
  padding: 0 0 8px;
  color: ${props => props.theme.grayTextColor};
  font-size: 14px;
  line-height: 21px;
` as OverridableComponent<ListItemTypeMap<{}, 'li'>>;

export default Payments;
